/**
 * quickEditTarget — which widget's quick-edit card is open: the small card
 * attached to the widget on the canvas (the level-editor layer), as opposed to
 * the Inspector's full form (settingsTarget).
 *
 * One editing surface at a time: opening the card closes the Inspector and
 * opening the Inspector closes the card. On a phone both are bottom sheets, and
 * two stacked sheets for one widget would be noise anywhere.
 */
import { useSyncExternalStore } from "react";

import { closeSettings, onSettingsOpened } from "./settingsTarget.js";

export interface QuickEditTarget {
  readonly gridId: string;
  readonly itemId: string;
}

let current: QuickEditTarget | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Open the quick-edit card of `itemId` (in `gridId`). */
export function openQuickEdit(gridId: string, itemId: string): void {
  closeSettings();
  if (current?.gridId === gridId && current.itemId === itemId) return;
  current = { gridId, itemId };
  emit();
}

export function closeQuickEdit(): void {
  if (!current) return;
  current = null;
  emit();
}

export function quickEditTarget(): QuickEditTarget | null {
  return current;
}

export function useQuickEditTarget(): QuickEditTarget | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => null,
  );
}

/** Test seam. */
export function resetQuickEditTarget(): void {
  current = null;
}

// The Inspector opening closes the card (the other half of "one at a time").
onSettingsOpened(closeQuickEdit);
